import React from 'react';
import PropTypes from 'prop-types';
import { Activity } from 'lucide-react';
import { AreaChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatCurrency, formatShortCurrency } from '../../utils/valuationDashboardUtils';

/**
 * EbitdaForecastChart - Displays revenue and EBITDA growth forecast with profit gap
 * Shows projected revenue, current EBITDA, and potential profit gap to industry high
 */
const EbitdaForecastChart = ({
  ebitdaForecastData,
  growthRate,
  ebitdaMargin,
  profitGap
}) => {
  return (
    <div className="bg-white rounded-xl shadow-md p-3 sm:p-5 border border-gray-100 hover:shadow-lg transition-all duration-300">
      <div className="mb-3 sm:mb-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="p-1.5 sm:p-2 bg-gradient-to-br from-purple-100 to-violet-100 rounded-lg">
            <Activity className="text-purple-600" size={16} />
          </div>
          <h2 className="text-base sm:text-lg font-bold text-gray-900">Revenue & EBITDA Growth</h2>
        </div>
        <p className="text-xs text-gray-500 pl-8 sm:pl-11">
          {ebitdaMargin}% EBITDA margin with {growthRate}% revenue growth
        </p>
      </div>

      <ResponsiveContainer width="100%" height={280} className="sm:h-80">
        <AreaChart data={ebitdaForecastData}>
          <defs>
            <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#22c55e" stopOpacity={0.2} />
              <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="colorProfitGap" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.7}/>
              <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.3}/>
            </linearGradient>
            <linearGradient id="colorCurrentEbitda" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#9333ea" stopOpacity={0.7} />
              <stop offset="95%" stopColor="#9333ea" stopOpacity={0.3} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 10 }}
            stroke="#9ca3af"
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fontSize: 10 }}
            stroke="#9ca3af"
            tickFormatter={(value) => formatShortCurrency(value)}
            width={60}
          />

          <Tooltip
            formatter={(value) => formatCurrency(value)}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '12px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}
            itemStyle={{ padding: '4px 0' }}
            labelStyle={{ fontWeight: 'bold', marginBottom: '8px' }}
            itemSorter={(item) => {
              const order = {
                revenue: 1,
                targetEbitda: 2,
                profitGapAmount: 3,
                currentEbitda: 4,
              };
              return order[item.dataKey] || 999;
            }}
          />

          {/* Revenue area */}
          <Area
            type="monotone"
            dataKey="revenue"
            stackID="revenue"
            stroke="#22c55e"
            strokeWidth={2}
            fill="url(#colorRevenue)"
            fillOpacity={0.4}
            name="Revenue"
          />

          {/* Target EBITDA reference line */}
          {profitGap?.industry_high_ebitda_margin > 0 && (
            <Line
              type="monotone"
              dataKey="targetEbitda"
              stroke="#ee4242"
              StackID="revenue"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={false}
              name="Target EBITDA"
            />
          )}

          {/* Stacked EBITDA components */}
          <Area
            type="monotone"
            dataKey="currentEbitda"
            stackId="ebitda"
            stroke="#7c3aed"
            strokeWidth={2}
            fill="url(#colorCurrentEbitda)"
            name="Current EBITDA"
          />

          {profitGap?.industry_high_ebitda_margin > 0 && (
            <Area
              type="monotone"
              dataKey="profitGapAmount"
              stackId="ebitda"
              stroke="#f59e0b"
              strokeWidth={0}
              fill="url(#colorProfitGap)"
              name="Profit Gap"
            />
          )}

        </AreaChart>
      </ResponsiveContainer>

      {/* Custom Legend with Tooltips */}
      <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-200">
        <div className="flex flex-wrap gap-2 sm:gap-4 justify-center text-xs">
          {/* Revenue */}
          <div className="group relative">
            <div className="flex items-center gap-1 sm:gap-1.5 cursor-help">
              <div className="w-4 sm:w-6 h-2 sm:h-3 bg-green-200 rounded opacity-70"></div>
              <span className="text-gray-700 font-medium text-xs">Revenue</span>
            </div>
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
              Projected total revenue based on growth rate
            </div>
          </div>

          {/* Target EBITDA Line */}
          {profitGap?.industry_high_ebitda_margin > 0 && (
            <div className="group relative">
              <div className="flex items-center gap-1 sm:gap-1.5 cursor-help">
                <svg width="20" height="2" className="mt-1 sm:w-24">
                  <line x1="0" y1="1" x2="20" y2="1" stroke="#ee4242" strokeWidth="2" strokeDasharray="5,5" />
                </svg>
                <span className="text-gray-700 font-medium text-xs">Target</span>
              </div>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                EBITDA at industry-high margin
              </div>
            </div>
          )}

          {/* Profit Gap */}
          {profitGap?.industry_high_ebitda_margin > 0 && (
            <div className="group relative">
              <div className="flex items-center gap-1 sm:gap-1.5 cursor-help">
                <div className="w-4 sm:w-6 h-2 sm:h-3 rounded" style={{ background: 'linear-gradient(to bottom, rgba(245, 158, 11, 0.7) 0%, rgba(245, 158, 11, 0.3) 100%)' }}></div>
                <span className="text-gray-700 font-medium text-xs">Gap</span>
              </div>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                Gap to reach industry-high {profitGap?.industry_high_ebitda_margin?.toFixed(1)}% margin (stacked)
              </div>
            </div>
          )}

          {/* Current EBITDA */}
          <div className="group relative">
            <div className="flex items-center gap-1 sm:gap-1.5 cursor-help">
              <div className="w-4 sm:w-6 h-2 sm:h-3 rounded" style={{ background: 'linear-gradient(to bottom, rgba(147, 51, 234, 0.7) 0%, rgba(147, 51, 234, 0.3) 100%)' }}></div>
              <span className="text-gray-700 font-medium text-xs">Current</span>
            </div>
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
              Current earnings at {ebitdaMargin}% margin (base layer)
            </div>
          </div>

        </div>
      </div>

      <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-gradient-to-r from-purple-50 to-violet-50 rounded-lg border border-purple-100">
        <p className="text-xs text-purple-900 font-medium">
          <span className="font-bold">Formula:</span> Revenue × EBITDA Margin = EBITDA / Gross Profit
        </p>
      </div>
    </div>
  );
};

EbitdaForecastChart.propTypes = {
  ebitdaForecastData: PropTypes.arrayOf(PropTypes.object).isRequired,
  growthRate: PropTypes.number.isRequired,
  ebitdaMargin: PropTypes.number.isRequired,
  profitGap: PropTypes.object
};

export default EbitdaForecastChart;
